<template>
  <div class="fund-confirm">
    <form @submit.prevent="fundRequest" class="fund-confirm__form">
      <div class="fund-confirm__summary">
        <div class="fund-confirm__row">
          <span class="fund-confirm__label">{{ swapCurrency ? "Converting" : "Amount" }}</span>
          <span class="fund-confirm__value" v-if="swapCurrency">
            {{ formatAmountToDollar(currentFundPayload.amount, 2, swapCurrency) }}
          </span>
          <span class="fund-confirm__value" v-else>
            {{ formatAmountToDollar(currentFundPayload?.amount, 2, currency) }}
          </span>
        </div>

        <div class="fund-confirm__row" v-if="swapCurrency">
          <span class="fund-confirm__label">To</span>
          <span class="fund-confirm__value">{{ currency }} Wallet</span>
        </div>

        <div class="fund-confirm__row" v-else>
          <div class="fund-confirm__fee-row">
            <span class="fund-confirm__label">Transaction Fee</span>
            <span class="fund-confirm__fee-info" v-if="feeDetails && fundOptions !== 'Crypto'">
              {{ feeDetails.ratePercentage }}
              <span v-if="feeDetails.isCapped" class="fund-confirm__fee-cap">
                (capped at {{ formatAmountToDollar(feeDetails.cap, 2, currency) }})
              </span>
            </span>
          </div>
          <span class="fund-confirm__value">
            {{ formatAmountToDollar(currentTransactionFee, 2, currency) }}
          </span>
        </div>

        <div class="fund-confirm__row" v-if="swapCurrency">
          <span class="fund-confirm__label">Exchange Rate</span>
          <span class="fund-confirm__value">
            1 {{ swapCurrency }} = {{ formatAmountToDollar(currencyRate, 4, currency) }}
          </span>
        </div>

        <div class="fund-confirm__row" v-else-if="fundOptions">
          <span class="fund-confirm__label">Payment Method</span>
          <span class="fund-confirm__value">{{ fundingMeans }}</span>
        </div>
      </div>

      <div class="fund-confirm__total">
        <span class="fund-confirm__total-label">
          {{ swapCurrency ? "You will receive" : "Total to Pay" }}
        </span>
        <span class="fund-confirm__total-value" v-if="swapCurrency">
          {{ formatAmountToDollar(swapConversion, 2, currency) }}
        </span>
        <span class="fund-confirm__total-value" v-else>
          {{ formatAmountToDollar(currentFundPayload?.totalAmount, 2, currency) }}
        </span>
      </div>

      <div class="fund-confirm__actions">
        <CustomButton :loading="loading" title="Fund Account" />
      </div>
    </form>
  </div>
</template>

<script>
import CustomButton from "@/components/general/BtnComponent";
import api from "@/utils/api.js";
import { convertToLocale, formatAmountToDollar } from "@/utils/helpers.js";
import fundPercent from "@/utils/js/fundPercent.js";
import responseHandler from "@/utils/responseHandler.js";
import { getItem, setItem } from "@/utils/storage.js";
import { mapActions, mapGetters, mapState } from "vuex";
export default {
  name: "FundConfirmationForm",

  emits: ["storeData", "close"],

  components: {
    CustomButton,
  },

  props: {
    fundOptions: { type: String, default: () => "", required: true },
  },

  data() {
    return {
      user: getItem("org_details"),
      fundPercent,
      customizations: {
        title: "GetEquity Payments",
        description: "Purchase of GUSD from GetEquity",
      },
      loading: false,
    };
  },

  inject: ["swapCurrency"],

  computed: {
    ...mapState({
      currency: (state) => state.organisationModule.currency,
      currencyRate: (state) => state.organisationModule.currencyRate,
      cardDetails: (state) => state.organisationModule.cardDetails,
    }),
    ...mapGetters(["smallLoading"]),
    ...mapGetters("fundingModule", [
      "transactionFee",
      "totalAmount",
      "feeBreakdown",
      "currentFundPayload",
    ]),

    currentTransactionFee() {
      return this.transactionFee(
        this.fundOptions,
        this.currentFundPayload?.amount,
        this.currency
      );
    },

    currentTotalAmount() {
      return this.totalAmount(
        this.fundOptions,
        this.currentFundPayload?.amount,
        this.currency
      );
    },

    feeDetails() {
      return this.feeBreakdown(
        this.fundOptions,
        this.currentFundPayload?.amount,
        this.currency
      );
    },

    fundingMeans() {
      let fundingMeans = null;
      switch (this.fundOptions) {
        case "Fund with debit card":
          fundingMeans = "Debit/Credit Card";
          break;
        case "Mobile Money":
          fundingMeans = "Mobile Money";
          break;
        case "Fund with bank transfer":
          fundingMeans = "Bank Transfer";
          break;
        case "Crypto":
          fundingMeans = "Cryptocurrency";
          break;
        case "Fund with Fintech Apps":
          fundingMeans = "Fintech App";
          break;
        case "P2P Transfers":
          fundingMeans = "P2P Transfer";
          break;
      }
      return fundingMeans;
    },
    swapConversion() {
      if (this.swapCurrency) {
        return this.currentFundPayload.amount * Number(this.currencyRate);
      }
      return 0;
    },
  },

  mounted() {
    if (!this.currentFundPayload) {
      this.$emit('storeData', 'fundAmountForm'); 
    }
  },

  methods: {
    ...mapActions("organisationModule", ["getWalletBalance", "convertWallet"]),
    ...mapActions(["showToast"]),

    formatAmountToDollar,
    convertToLocale,

    ...mapActions("fundingModule", [
      "calculateFee",
      "initializeFunding",
      "updateFundingDetails",
      "clearFunding",
    ]),

    async calculateFee() {
      try {
        const details = getItem("fundPayload");
        if (!details) return;

        await this.updateFundingDetails({
          fundOptions: this.fundOptions,
          amount: details.amount,
          currency: this.currency,
          tokens: details.tokens,
        });
      } catch (error) {
        this.showToast({
          type: "error",
          message: "Failed to calculate fees",
        });
      }
    },
    
    async fundRequest() {
      this.loading = true;
      switch (this.fundOptions) {
        case "Mobile Money":
          this.flutterwaveFunding();
          break;
        case "Fund with debit card":
          this.$emit("storeData", "fundConfirmationForm");
          break;
        case "":
          this.$emit("storeData", "fundProvider");
          break;
        case "Fund with bank transfer":
          this.$emit("storeData", "fundConfirmationForm");
          break;
        case "Fund with Fintech Apps":
          this.$emit("storeData", "fundConfirmationForm");
          break;
        case "Crypto":
          this.cryptoFunding();
          break;
        case "USD Wallet":
          this.fundFromWallet();
          break;
        case "NGN Wallet":
          this.fundFromWallet();
          break;
        case "KES Wallet":
          this.fundFromWallet();
          break;
        case "P2P Transfers":
          this.accrueFunding();
          break;
        default:
          break;
      }
    },

    async fundFromWallet() {
      const payload = {
        amount: this.currentFundPayload.totalAmount,
        from: this.swapCurrency,
        to: this.currency,
      };
      try {
        const response = await this.convertWallet(payload);
        const { message } = response.data;
        this.loading = false;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
        await this.getWalletBalance();
        this.$emit("close", "Fund Wallet");
      } catch (error) {
        this.loading = false;
      }
    },

    async flutterwaveFunding() {
      const data = {
        amount: this.currentFundPayload.totalAmount,
        currency: this.currency,
        customer: {
          name: this.user.name
            ? this.user.name
            : `${this.user.fname} ${this.user.lname}`,
          email: this.user.email,
        },

        customizations: this.customizations,
        meta: {
          entity: "user",
        },

        paymentReference: "",
        redirect_url: window.location.href.includes("wallet")
          ? window.location.origin + "/dashboard/wallet"
          : window.location.origin + "/dashboard/overview",
      };
      try {
        const response = await api.fundWithProvider({
          data,
          type: "flutterwave",
        });
        window.location = response.data.data.link;
        this.loading = false;
      } catch (error) {
        responseHandler.handleError(error);
        this.loading = false;
      }
    },

    async thepeerFunding() {
      // eslint-disable-next-line no-undef
      const thePeerInstance = new ThePeer.directCharge({
        publicKey: `${process.env.VUE_APP_THE_PEER}`,
        amount: this.currentFundPayload.totalAmount * 100,
        currency: this.currency,
        userReference: this.user.thepeer_reference,
        meta: {
          tokens: Number(this.currentFundPayload.tokens),
          entity: "user",
        },

        onError: function (error) {
          responseHandler.handleError(error);
        },

        onClose: () => {
          this.getWalletBalance();
          this.$emit("close", "Fund Wallet");
        },

        onSuccess: () => {
          this.getWalletBalance();
          this.$emit("close", "Fund Wallet");
        },
      });
      thePeerInstance.setup();
      thePeerInstance.open();
    },

    async cryptoFunding() {
      const tokens = this.currentFundPayload.totalAmount;
      const data = {
        amount: tokens,
        metadata: {
          tokens: Number(tokens),
          entity: "user",
        },

        email: this.user.email,
        name: this.user.name
          ? this.user.name
          : `${this.user.fname} ${this.user.lname}`,
        currency: "USD",
        redirect_url: window.location.href.includes("wallet")
          ? "https://staging--company-dealroom.netlify.app" +
            "/dashboard/wallet"
          : "https://staging--company-dealroom.netlify.app" +
            "/dashboard/overview",
      };
      try {
        const response = await api.fundWithProvider({
          data,
          type: "blockradar",
        });
        window.location = response.data.data.link;
        this.loading = false;
      } catch (error) {
        responseHandler.handleError(error);
        this.loading = false;
      }
    },

    async accrueFunding() {
      const data = {
        amount: this.currentFundPayload.totalAmount,
        currency: this.currency,
        customer: {
          name: this.user.name
            ? this.user.name
            : `${this.user.fname} ${this.user.lname}`,
          email: this.user.email,
        },
        customizations: this.customizations,
        meta: {
          entity: "user",
        },
        redirect_url: window.location.href.includes("wallet")
          ? "https://staging--company-dealroom.netlify.app" +
            "/dashboard/wallet"
          : "https://staging--company-dealroom.netlify.app" +
            "/dashboard/overview",
      };
      try {
        const response = await api.fundWithProvider({
          data,
          type: "cashramp",
        });
        window.location = response.data.data.link;
        this.loading = false;
      } catch (error) {
        responseHandler.handleError(error);
        this.loading = false;
      }
    },

    async startButtonFunding() {
      const data = {
        amount: this.currentFundPayload.totalAmount,
        currency: this.currency,
        customer: {
          name: this.user.name
            ? this.user.name
            : `${this.user.fname} ${this.user.lname}`,
          email: this.user.email,
        },
        customizations: this.customizations,
        meta: {
          entity: "user",
        },
        redirect_url: window.location.href.includes("wallet")
          ? "https://staging--company-dealroom.netlify.app" +
            "/dashboard/wallet"
          : "https://staging--company-dealroom.netlify.app" +
            "/dashboard/overview",
      };
      try {
        const response = await api.fundWithProvider({
          data,
          type: "startbutton",
        });
        window.location = response.data.data;
        this.loading = false;
      } catch (error) {
        responseHandler.handleError(error);
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fund-confirm {
  padding: 8px 24px;

  &__form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &__summary {
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    padding: 20px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 14px 0;
    border-bottom: 1px solid #E2E8F0;

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    &:first-child {
      padding-top: 0;
    }
  }

  &__fee-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
  }

  &__value {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    text-align: right;
  }

  &__fee-info {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #94A3B8;
  }

  &__fee-cap {
    color: #F59E0B;
  }

  &__total {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 20px;
    background: linear-gradient(135deg, #4D84FF 0%, #6B9AFF 100%);
    border-radius: 12px;
  }

  &__total-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.8);
  }

  &__total-value {
    font-family: 'DM Sans', sans-serif;
    font-size: 28px;
    font-weight: 500;
    color: white;
  }

  &__actions {
    display: flex;
    justify-content: center;

    :deep(.btn) {
      width: 100%;
      height: 48px;
      border-radius: 8px;
      font-family: 'DM Sans', sans-serif;
      font-size: 15px;
      font-weight: 500;
    }
  }
}
</style>
